/**
 * posHardware.ts — Lógica de cuándo abrir gaveta + impresión térmica multiplataforma.
 *
 * QUÉ HACE:
 *  - Centraliza la lógica "¿hay efectivo físico?" para disparar la gaveta
 *  - Construye los bytes ESC/POS de cada tipo de ticket y los envía a través de
 *    hardwareAdapter, que resuelve la plataforma: Capacitor (APK) o Tauri TCP/USB (Desktop)
 *  - Provee buildSimpleTestPageEscPos() para la página de prueba del POS
 *
 * QUÉ EXPORTA:
 *  - saleRequiresCashDrawer, shouldOpenDrawerForSale, movimientoRequiresCashDrawer,
 *    abonoCreditoRequiresCashDrawer, openCashDrawer, openCashDrawerForAbonoCredito
 *  - tryPrintThermalSaleReceipt, tryPrintThermalCierre, tryPrintThermalJornada,
 *    tryPrintThermalMovimiento, tryPrintThermalPedido, tryPrintThermalComanda,
 *    tryPrintThermalTestPage,
 *    tryPrintThermalWeighLabel (etiqueta de pesaje; usa la impresora de
 *    etiquetas si está configurada como separada),
 *    tryPrintThermalLabelBatch (lote de etiquetas de producto con corte entre
 *    cada una; usa la impresora de etiquetas)
 *  - buildSimpleTestPageEscPos, canPrintOnCurrentPlatform (indica si la
 *    plataforma actual puede imprimir), isPrinterUnavailable (no hay impresora
 *    configurada o no es accesible desde esta plataforma)
 *
 * NOTA: cuando no hay impresora disponible, cada tryPrint* retorna false SIN
 * toast. El aviso/confirmación lo gestionan los callers con
 * askPrintWithoutPrinter (printerGateStore → PrinterUnavailableModal global),
 * que bloquea la acción final hasta que el usuario confirma.
 *
 * QUÉ NO DEBE HACER:
 *  - No registrar ventas ni movimientos (eso sigue en cajaService / hooks)
 *  - No llamar directamente al plugin AltenotPos para imprimir; usar hardwareAdapter
 */

import { logger } from "@/lib/logger";
import toast from "react-hot-toast";
import { isNative, isDesktop, haptics } from "@/lib/platform";
import { AltenotPos } from "./altenotPos";
import type { PaymentLine } from "@/features/caja/hooks/usePaymentView";
import {
  isPosPrinterConfigured,
  loadPosPrinterConfigFromStorage,
  resolvePrinterConfig,
} from "./posPrinterConfig";
import {
  buildSaleTicketEscPosAsync,
  type SaleTicketEscPosInput,
} from "@/features/caja/utils/saleTicketEscPos";
import {
  buildCierreEscPos,
  buildMovimientoEscPos,
  type CierreEscPosInput,
  type MovimientoEscPosInput,
} from "@/features/caja/utils/cierreTicketEscPos";
import {
  buildJornadaEscPos,
  type JornadaEscPosInput,
} from "@/features/caja/utils/jornadaTicketEscPos";
import type { Pedido } from "@/features/tienda/types/tienda.types";
import type { CartItem, Producto } from "@/types";
import { hardwareAdapter } from "@/lib/platform/hardwareAdapter";
import {
  buildProductLabelEscPos,
  type ProductLabelOptions,
} from "@/features/inventory/utils/productLabelEscPos";
import {
  buildBatchProductLabelEscPos,
  type BatchLabelEntry,
} from "@/features/inventory/utils/buildBatchProductLabelEscPos";
import type { LabelDesign } from "@/features/inventory/utils/labelDesign";
import {
  buildWeighLabelEscPosAsync,
  type WeighLabelEscPosInput,
  buildOrderLabelEscPosAsync,
  type OrderLabelEscPosInput,
} from "@/features/caja/services/ticketRender.service";
import { buildComandaEscPos } from "@/features/mesas/utils/comandaEscPos";
import type { ComandaMesa, OrdenMesa } from "@/types/mesas";

export type MovimientoCajaTipo = "retiro" | "gasto" | "ingreso" | "abrir_caja";

// ── Reglas de apertura de gaveta ──────────────────────────────────────────────

/** Venta: gaveta solo si el cobro incluye línea(s) en efectivo. */
export function saleRequiresCashDrawer(
  paymentMode: "simple" | "split",
  lines: PaymentLine[],
): boolean {
  if (lines.length === 0) return false;
  if (paymentMode === "simple") {
    return lines[0]?.method === "efectivo";
  }
  return lines.some((l) => l.method === "efectivo");
}

/** La gaveta solo se abre cuando el cobro incluye efectivo (para dar cambio). */
export function shouldOpenDrawerForSale(
  paymentMode: "simple" | "split",
  lines: PaymentLine[],
): boolean {
  return saleRequiresCashDrawer(paymentMode, lines);
}

/**
 * Movimientos: retiro y gasto siempre implican efectivo físico en caja.
 * Ingreso solo si el medio es efectivo (entra a caja física).
 * `abrir_caja` es una apertura de gaveta registrada: siempre abre.
 */
export function movimientoRequiresCashDrawer(
  tipo: MovimientoCajaTipo,
  metodoPagoIngreso?: "efectivo" | "nequi" | "daviplata" | "tarjeta",
): boolean {
  if (tipo === "retiro" || tipo === "gasto" || tipo === "abrir_caja") {
    return true;
  }
  return metodoPagoIngreso === "efectivo";
}

/** Cobro de cuenta de crédito: gaveta solo si el abono entra en efectivo físico. */
export function abonoCreditoRequiresCashDrawer(metodoPago: string): boolean {
  return movimientoRequiresCashDrawer(
    "ingreso",
    (metodoPago || "").toLowerCase() as
      | "efectivo"
      | "nequi"
      | "daviplata"
      | "tarjeta",
  );
}

export async function openCashDrawerForAbonoCredito(
  metodoPago: string,
): Promise<void> {
  if (!abonoCreditoRequiresCashDrawer(metodoPago)) return;
  await openCashDrawer({
    context: "movimiento",
    movimientoTipo: "ingreso",
    reason: "Abono cuenta de crédito en efectivo — ingreso a caja física",
  });
}

/**
 * Imprime una etiqueta de producto.
 */
export async function tryPrintThermalProductLabel(
  product: Producto,
  options: ProductLabelOptions,
): Promise<boolean> {
  try {
    if (!canPrintOnCurrentPlatform()) {
      return false;
    }

    const config = loadPosPrinterConfigFromStorage();
    if (!isPosPrinterConfigured(config)) {
      return false;
    }

    const paper = config.paperWidthMm === 80 ? 80 : 58;
    const bytes = await buildProductLabelEscPos(product, options, paper);

    return sendThermalBytes(
      bytes,
      "Etiqueta enviada a la impresora",
      "pos-label-print",
      "pos-label-print-err",
    );
  } catch (err) {
    logger.error("[POS] Label Print Error:", err);
    toast.error("Fallo al generar etiqueta");
    return false;
  }
}

/**
 * Imprime un lote de etiquetas de producto en un solo trabajo ESC/POS, con
 * corte automático entre etiquetas. Usa la impresora de etiquetas configurada
 * (`labelPrinterMode`: misma impresora, separada u off) y respeta su ancho.
 */
export async function tryPrintThermalLabelBatch(
  entries: BatchLabelEntry[],
  design: LabelDesign,
): Promise<boolean> {
  if (entries.length === 0) return false;
  const cfg = loadPosPrinterConfigFromStorage();
  if (cfg.labelPrinterMode === "off") return false;
  if (!canPrintOnCurrentPlatform("labels")) return false;
  const effective = resolvePrinterConfig(cfg, "labels");
  if (!isPosPrinterConfigured(effective)) return false;

  const paper = effective.paperWidthMm === 80 ? 80 : 58;
  const bytes = buildBatchProductLabelEscPos(entries, design, paper);

  return sendThermalBytes(
    bytes,
    "Etiquetas enviadas a la impresora",
    "pos-label-print",
    "pos-label-print-err",
    "labels",
  );
}

export interface OpenCashDrawerMeta {
  context?: "sale" | "movimiento" | "cierreCaja";
  reason?: string;
  movimientoTipo?: MovimientoCajaTipo;
  cierreFase?: "arqueo" | "final";
}

/**
 * Envuelve una promesa con un timeout duro. Garantiza que llamadas nativas
 * colgadas (p. ej. plugin Capacitor no registrado) no bloqueen el flujo de
 * la aplicación por más de `ms` milisegundos.
 */
function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  context: string,
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${context} timeout`)), ms),
    ),
  ]);
}

/**
 * Pulso de apertura de gaveta vía hardwareAdapter.
 * · APK: AltenotPos.openCashDrawer() (con timeout 3s)
 * · Desktop: Tauri → TCP socket → pulso ESC/POS (con timeout 3s)
 * · Web: no-op
 *
 * CRÍTICO: nunca debe bloquear los flujos de caja/venta. El timeout evita
 * que un plugin nativo mal registrado deje la UI congelada.
 */
export async function openCashDrawer(meta?: OpenCashDrawerMeta): Promise<void> {
  if (!isNative && !isDesktop) return;

  const reason =
    meta?.reason ||
    (meta?.context === "cierreCaja"
      ? meta?.cierreFase === "final"
        ? "Cierre de caja — finalizar y retirar efectivo"
        : "Cierre de caja — arqueo / conteo físico"
      : meta?.context === "sale"
        ? "Venta con efectivo"
        : meta?.movimientoTipo
          ? `Movimiento: ${meta.movimientoTipo}`
          : "Caja física");

  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    toast.error(
      "Sin impresora POS configurada. Ve a tu perfil → Impresora y gaveta (POS).",
      { duration: 4500, id: "pos-no-printer" },
    );
    if (isNative) {
      // No bloqueante: la falta de configuración no debe detener el flujo.
      void haptics.medium();
    }
    return;
  }

  try {
    if (isDesktop) {
      const result = await withTimeout(
        hardwareAdapter.printer.openCashDrawer(),
        3000,
        "openCashDrawer-desktop",
      );
      if (!result.opened) {
        toast.error(result.error || "No se pudo abrir la gaveta", {
          duration: 4500,
          id: "pos-drawer-err",
        });
      }
      return;
    }
    // APK: timeout duro porque registerPlugin puede devolver un proxy que
    // nunca resuelve si el plugin nativo no está registrado en el proyecto.
    await withTimeout(
      AltenotPos.openCashDrawer({ reason }),
      3000,
      "openCashDrawer-apk",
    );
  } catch (err) {
    logger.warn("[POS] openCashDrawer falló (no bloqueante):", err);
    if (isNative) {
      void haptics.medium();
    }
  }
}

// ── Helpers de impresión ──────────────────────────────────────────────────────

/**
 * Indica si la plataforma actual puede imprimir de forma térmica.
 * · Native: siempre (si hay impresora configurada)
 * · Desktop: siempre (TCP, USB puerto o BT COM)
 * · Web: solo si transport === "usb" y hay usbDeviceKey (WebUSB) o si hay relay configurado
 */
export function canPrintOnCurrentPlatform(
  target: "tickets" | "labels" = "tickets",
): boolean {
  if (isNative || isDesktop) return true;
  const cfg = resolvePrinterConfig(loadPosPrinterConfigFromStorage(), target);

  // WebUSB
  if (cfg.transport === "usb" && cfg.usbDeviceKey.trim().length > 0)
    return true;

  // Relay (para impresoras de red desde el navegador)
  if (cfg.transport === "network" && cfg.networkRelayUrl.trim().length > 0)
    return true;

  return false;
}

/**
 * Indica si no hay impresora disponible en este equipo: no está configurada o
 * el transporte configurado no es accesible desde la plataforma actual.
 * Los callers la usan para decidir si deben pedir confirmación antes del
 * fallback de impresión (askPrintWithoutPrinter / PrinterUnavailableModal).
 */
export function isPrinterUnavailable(): boolean {
  return (
    !canPrintOnCurrentPlatform() ||
    !isPosPrinterConfigured(loadPosPrinterConfigFromStorage())
  );
}

/**
 * Construye una página de prueba ESC/POS mínima (compatible con todos los
 * anchos de papel). Útil para verificar la conexión con la impresora.
 */
export function buildSimpleTestPageEscPos(
  paperWidthMm: 58 | 80 = 58,
): Uint8Array {
  const line = paperWidthMm === 80 ? "─".repeat(42) : "─".repeat(32);
  const enc = new TextEncoder();
  const parts = [
    "\x1B\x40", // ESC @ — reset
    "\x1B\x61\x01", // ESC a 1 — centrar
    "\x1B\x45\x01", // ESC E 1 — negrita ON
    "ALTENOT TECH\n",
    "\x1B\x45\x00", // ESC E 0 — negrita OFF
    "Pagina de prueba\n",
    `${line}\n`,
    "\x1B\x61\x00", // ESC a 0 — izquierda
    "Si lees este texto,\n",
    "la impresora esta conectada\n",
    "y funcionando correctamente.\n",
    `${line}\n`,
    "\n\n\n",
    "\x1D\x56\x42\x00", // GS V B 0 — corte parcial
  ];
  return enc.encode(parts.join(""));
}

// ── Función interna para enviar bytes con hardwareAdapter ─────────────────────

/**
 * Envía bytes ESC/POS via hardwareAdapter (resuelve plataforma internamente).
 * Retorna true si la impresión fue exitosa, false si falló (y ya mostró toast).
 */
async function sendThermalBytes(
  bytes: Uint8Array,
  successMsg: string,
  successId: string,
  errorId: string,
  target: "tickets" | "labels" = "tickets",
): Promise<boolean> {
  const result = await hardwareAdapter.printer.printRaw(bytes, target);
  if (result.success) {
    toast.success(successMsg, { id: successId });
    return true;
  }
  const errMsg =
    result.error?.startsWith("Sin impresora") ||
    result.error?.startsWith("Transporte") ||
    result.error?.startsWith("Para abrir")
      ? result.error
      : result.error || "No se pudo imprimir en la térmica";
  toast.error(errMsg, { duration: 4500, id: errorId });
  return false;
}

// ── Funciones de impresión por tipo de documento ──────────────────────────────

type ThermalPayload = Omit<SaleTicketEscPosInput, "paperWidthMm"> & {
  paperWidthMm?: 58 | 80;
};

/**
 * Envía el recibo de venta a la térmica (APK + Desktop).
 * Retorna true si se imprimió por la térmica; false si no (UI usa fallback PDF).
 */
export async function tryPrintThermalSaleReceipt(
  payload: ThermalPayload,
): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) {
    return false;
  }
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    return false;
  }
  const paper = cfg.paperWidthMm === 80 ? 80 : 58;
  const bytes = await buildSaleTicketEscPosAsync({
    ...payload,
    paperWidthMm: paper,
  });
  return sendThermalBytes(
    bytes,
    "Ticket enviado a la impresora",
    "pos-ticket-print",
    "pos-ticket-print-err",
  );
}

/**
 * Imprime un cierre de caja en la térmica (APK + Desktop).
 * Retorna false para que el caller use window.print() como fallback.
 */
export async function tryPrintThermalCierre(
  payload: Omit<CierreEscPosInput, "paperWidthMm"> & {
    paperWidthMm?: 58 | 80;
  },
): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) {
    return false;
  }
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    return false;
  }
  const paper = cfg.paperWidthMm === 80 ? 80 : 58;
  const bytes = buildCierreEscPos({ ...payload, paperWidthMm: paper });
  return sendThermalBytes(
    bytes,
    "Cierre enviado a la impresora",
    "pos-cierre-print",
    "pos-cierre-print-err",
  );
}

/**
 * Imprime el resumen global de jornada en la térmica (APK + Desktop).
 * Retorna false para que el caller use window.print() como fallback.
 */
export async function tryPrintThermalJornada(
  payload: JornadaEscPosInput,
): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) {
    return false;
  }
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    return false;
  }
  const paper = cfg.paperWidthMm === 80 ? 80 : 58;
  const bytes = buildJornadaEscPos({ ...payload, paperWidthMm: paper });
  return sendThermalBytes(
    bytes,
    "Resumen de jornada enviado a la impresora",
    "pos-jornada-print",
    "pos-jornada-print-err",
  );
}

/**
 * Imprime un movimiento de caja (retiro / gasto / ingreso) en la térmica.
 * Diseñado para llamarse automáticamente tras registrar el movimiento.
 */
export async function tryPrintThermalMovimiento(
  payload: Omit<MovimientoEscPosInput, "paperWidthMm"> & {
    paperWidthMm?: 58 | 80;
  },
): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) {
    return false;
  }
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    return false;
  }
  const paper = cfg.paperWidthMm === 80 ? 80 : 58;
  const bytes = buildMovimientoEscPos({ ...payload, paperWidthMm: paper });
  return sendThermalBytes(
    bytes,
    "Comprobante enviado a la impresora",
    "pos-mov-print",
    "pos-mov-print-err",
  );
}

/**
 * Imprime la etiqueta de pesaje en la térmica (etiquetas o recibos según
 * `labelPrinterMode`). Retorna true si se imprimió; false si está desactivada,
 * no hay impresora disponible o falló la impresión (toast de error).
 */
export async function tryPrintThermalWeighLabel(
  payload: Omit<WeighLabelEscPosInput, "paperWidthMm"> & {
    paperWidthMm?: 58 | 80;
  },
): Promise<boolean> {
  const cfg = loadPosPrinterConfigFromStorage();
  if (cfg.labelPrinterMode === "off") return false;
  if (!canPrintOnCurrentPlatform("labels")) return false;
  const eff = resolvePrinterConfig(cfg, "labels");
  if (!isPosPrinterConfigured(eff)) return false;
  const paper = payload.paperWidthMm ?? eff.paperWidthMm;
  const bytes = await buildWeighLabelEscPosAsync({
    ...payload,
    paperWidthMm: paper,
  });
  return sendThermalBytes(
    bytes,
    "Etiqueta enviada a la impresora",
    "pos-label-print",
    "pos-label-print-err",
    "labels",
  );
}

/**
 * Imprime la etiqueta RESUMEN DE PEDIDO (todo el carrito en UNA etiqueta)
 * en la térmica (etiquetas o recibos según `labelPrinterMode`).
 * Retorna true si se imprimió; false si está desactivada, no hay impresora
 * disponible o falló la impresión (toast de error).
 */
export async function tryPrintThermalOrderLabel(
  payload: Omit<OrderLabelEscPosInput, "paperWidthMm"> & {
    paperWidthMm?: 58 | 80;
  },
): Promise<boolean> {
  const cfg = loadPosPrinterConfigFromStorage();
  if (cfg.labelPrinterMode === "off") return false;
  if (!canPrintOnCurrentPlatform("labels")) return false;
  const eff = resolvePrinterConfig(cfg, "labels");
  if (!isPosPrinterConfigured(eff)) return false;
  const paper = payload.paperWidthMm ?? eff.paperWidthMm;
  const bytes = await buildOrderLabelEscPosAsync({
    ...payload,
    paperWidthMm: paper,
  });
  return sendThermalBytes(
    bytes,
    "Etiqueta del pedido enviada a la impresora",
    "pos-order-label-print",
    "pos-order-label-print-err",
    "labels",
  );
}

/**
 * Imprime una comanda de cocina (orden de mesa) en la térmica.
 * Retorna true si se imprimió; false si no hay impresora disponible.
 */
export async function tryPrintThermalComanda(
  orden: OrdenMesa,
  comanda?: ComandaMesa,
): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) {
    return false;
  }
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    return false;
  }
  const paper = cfg.paperWidthMm === 80 ? 80 : 58;
  const bytes = buildComandaEscPos({ orden, comanda, paperWidthMm: paper });
  return sendThermalBytes(
    bytes,
    "Comanda enviada a la impresora",
    "pos-comanda-print",
    "pos-comanda-print-err",
  );
}

/**
 * Construye el payload de venta a partir de un `Pedido` y lo envía a la
 * térmica. Reutiliza buildSaleTicketEscPosAsync para formato idéntico a recibo.
 */
export async function tryPrintThermalPedido(pedido: Pedido): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) {
    return false;
  }
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) {
    return false;
  }
  const paper = cfg.paperWidthMm === 80 ? 80 : 58;

  const cart = pedido.items.map(
    (p) =>
      ({
        nombre: p.nombre,
        precio: p.precio,
        quantity: p.cantidad,
        peso: p.peso,
        ventaPorPeso: p.ventaPorPeso,
        variantesSeleccionadas: p.variantesSeleccionadas,
      }) as unknown as CartItem,
  );

  const bytes = await buildSaleTicketEscPosAsync({
    cart,
    total: pedido.total,
    subtotal: pedido.total,
    paymentMethod: pedido.cliente.metodoPago,
    cashReceived: pedido.total,
    change: 0,
    note: pedido.cliente.notas,
    numeroVenta: pedido.numero,
    paperWidthMm: paper,
    ticketDate: new Date(pedido.fechaCreacion),
  });

  return sendThermalBytes(
    bytes,
    "Pedido enviado a la impresora",
    "pos-pedido-print",
    "pos-pedido-print-err",
  );
}

/**
 * Imprime una página de prueba (APK + Desktop).
 * En APK usa AltenotPos.printTestPage() (formateado por el plugin nativo).
 * En Desktop construye buildSimpleTestPageEscPos() y lo envía por TCP/USB.
 */
export async function tryPrintThermalTestPage(
  paperWidthMm?: 58 | 80,
): Promise<boolean> {
  if (!canPrintOnCurrentPlatform()) return false;
  const cfg = loadPosPrinterConfigFromStorage();
  if (!isPosPrinterConfigured(cfg)) return false;

  if (isNative) {
    try {
      await AltenotPos.printTestPage();
      toast.success("Impresión de prueba enviada", { id: "pos-test-print" });
      return true;
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "No se pudo imprimir la página de prueba";
      toast.error(msg, { duration: 4500, id: "pos-test-print-err" });
      return false;
    }
  }

  // Desktop y Web: construir página y enviar via hardwareAdapter
  const paper = paperWidthMm ?? (cfg.paperWidthMm === 80 ? 80 : 58);
  const bytes = buildSimpleTestPageEscPos(paper);
  return sendThermalBytes(
    bytes,
    "Página de prueba enviada a la impresora",
    "pos-test-print",
    "pos-test-print-err",
  );
}
